import { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import LoadingButton from '@mui/lab/LoadingButton';
// components
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

export default function WebcamDialog({ open, onClose, onCapture, uploadSource, accept }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);

  const [stream, setStream] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedVideo, setCapturedVideo] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const isImageAccepted = Object.keys(accept || {}).some(key => key.includes('image'));
  const isVideoAccepted = Object.keys(accept || {}).some(key => key.includes('video'));
  
  const [isVideoMode, setIsVideoMode] = useState(isVideoAccepted && !isImageAccepted);

  const streamRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setStream(null);
  }, []);

  const startCamera = useCallback(async () => {
    if (streamRef.current) return; // Already running

    try {
      setLoading(true);
      setError('');
      setCapturedImage(null);
      setCapturedVideo(null);
      setIsRecording(false);
      setRecordingTime(0);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Your browser does not support camera access or you are not using a secure connection (HTTPS).');
      }

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: isVideoMode, // Only request audio if in video mode
      });

      streamRef.current = mediaStream;
      setStream(mediaStream);
      setLoading(false);
    } catch (err) {
      console.error('Camera Access Error:', err);
      setError(err.message || 'Camera access denied or not found. Please check permissions.');
      setLoading(false);
    }
  }, [isVideoMode]);

  useEffect(() => {
    if (open && !capturedImage && !capturedVideo) {
      startCamera();
    } else if (!open) {
      stopCamera();
    }
    return () => {
      if (!open) stopCamera(); // Cleanup on close
    };
  }, [open, capturedImage, capturedVideo, startCamera, stopCamera]);

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Timer for recording
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => {
        if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording]);

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    
    // Mirror the image
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    
    ctx.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(dataUrl);
    stopCamera();
  };

  const handleStartRecording = () => {
    if (!streamRef.current) return;

    const chunks = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') 
        ? 'video/webm;codecs=vp9' 
        : 'video/webm';
    
    const recorder = new MediaRecorder(streamRef.current, { mimeType });
    
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/webm' });
      const videoUrl = URL.createObjectURL(blob);
      setCapturedVideo({ blob, url: videoUrl });
    };

    mediaRecorderRef.current = recorder;
    recorder.start();
    setIsRecording(true);
    setRecordingTime(0);
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      stopCamera();
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setCapturedVideo(null);
    startCamera();
  };

  const handleConfirm = async () => {
    if ((!capturedImage && !capturedVideo) || loading) return;

    try {
      setLoading(true);
      setError('');

      let finalFile = null;

      if (capturedVideo) {
        const file = new File([capturedVideo.blob], `webcam_${Date.now()}.webm`, { type: 'video/webm' });
        Object.assign(file, {
          preview: capturedVideo.url,
        });
        finalFile = file;
      } else {
          // IMAGE LOGIC
          let finalImage = capturedImage;

          // Handle watermarking if it's a camera_location source
          if (uploadSource === 'camera_location') {
            const canvas = document.createElement('canvas');
            const img = new Image();
            
            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              img.src = capturedImage;
            });

            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);

            // Fetch Location
            let locationText = 'Fetching Location...';
            let subText = '';
            let locationMetadata = null;
            try {
              const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                  enableHighAccuracy: true,
                  timeout: 10000,
                });
              });
              const { latitude, longitude, accuracy } = position.coords;
              locationText = `LAT: ${latitude.toFixed(6)} | LNG: ${longitude.toFixed(6)}`;
              locationMetadata = {
                latitude,
                longitude,
                accuracy,
                timestamp: position.timestamp,
              };
              
              // Try to get address (Reverse Geocoding)
              try {
                const geoRes = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
                const geoData = await geoRes.json();
                subText = geoData.display_name || '';
                if (geoData.display_name) {
                    locationMetadata.address = geoData.display_name;
                }
              } catch (e) {
                subText = 'Address not available';
              }
            } catch (e) {
                locationText = 'Location Access Denied';
                console.error('Location Error:', e);
            }

            // Draw Watermark
            const padding = 20;
            const barHeight = 80;
            ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
            ctx.fillRect(0, canvas.height - barHeight, canvas.width, barHeight);

            ctx.fillStyle = '#FFFFFF';
            ctx.textAlign = 'left';
            
            // Main location text (Lat/Long)
            ctx.font = 'bold 18px Arial';
            ctx.fillText(locationText, padding, canvas.height - 50);

            // Address (smaller) - truncate if too long
            ctx.font = '14px Arial';
            const maxWidth = canvas.width - (padding * 2);
            const truncatedAddress = subText.length > 80 ? `${subText.substring(0, 80)}...` : subText;
            ctx.fillText(truncatedAddress, padding, canvas.height - 30);

            // Date & Time
            ctx.font = 'italic 14px Arial';
            ctx.fillText(new Date().toLocaleString(), padding, canvas.height - 10);

            finalImage = canvas.toDataURL('image/jpeg', 0.9);
            
            // Store metadata on file if available
            if (locationMetadata) {
                window.__lastWebcamLocation = locationMetadata;
            }
          }

          // Convert result to File
          const res = await fetch(finalImage);
          const blob = await res.blob();
          const file = new File([blob], `webcam_${Date.now()}.jpg`, { type: 'image/jpeg' });
          
          // Add preview for UI consistency
          Object.assign(file, {
            preview: finalImage,
            ...(window.__lastWebcamLocation && { location: window.__lastWebcamLocation })
          });
          finalFile = file;
      }

      // Clean up global temp
      delete window.__lastWebcamLocation;

      onCapture(finalFile);
      onClose();
    } catch (err) {
      console.error('Capture confirmation error:', err);
      setError('Failed to process captured content.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const renderContent = () => {
    if (capturedImage) {
      return (
        <Box
          component="img"
          src={capturedImage}
          alt="Captured"
          sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      );
    }

    if (capturedVideo) {
      return (
        <Box
          component="video"
          src={capturedVideo.url}
          controls
          sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        >
          <track kind="captions" />
        </Box>
      );
    }

    return (
      <Box
        ref={videoRef}
        component="video"
        autoPlay
        playsInline
        muted
        sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', transform: 'scaleX(-1)' }}
      />
    );
  };

  const renderActions = () => {
    if (capturedImage || capturedVideo) {
      return (
        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" color="primary" onClick={handleRetake} disabled={loading}>
            Retake
          </Button>
          <LoadingButton variant="contained" color="primary" loading={loading} onClick={handleConfirm}>
            Use {capturedVideo ? 'Video' : 'Photo'}
          </LoadingButton>
        </Stack>
      );
    }

    if (isVideoMode) {
      return (
        <IconButton
          onClick={isRecording ? handleStopRecording : handleStartRecording}
          disabled={loading || !!error}
          sx={{
            bgcolor: isRecording ? 'error.main' : 'primary.main',
            color: 'white',
            width: 64,
            height: 64,
            '&:hover': { bgcolor: isRecording ? 'error.dark' : 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
          }}
        >
          <Iconify icon={isRecording ? 'solar:stop-bold' : 'solar:videocamera-record-bold'} width={32} />
        </IconButton>
      );
    }

    return (
      <IconButton
        onClick={handleCapture}
        disabled={loading || !!error}
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          width: 56,
          height: 56,
          '&:hover': { bgcolor: 'primary.dark' },
          '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
        }}
      >
        <Iconify icon="solar:camera-bold" width={32} />
      </IconButton>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify icon={isVideoMode ? 'solar:videocamera-record-bold' : 'solar:camera-bold'} width={24} />
          <Typography variant="h6">{isVideoMode ? 'Record Video' : 'Take Photo'}</Typography>
        </Stack>

        {isImageAccepted && isVideoAccepted && !capturedImage && !capturedVideo && (
          <Stack direction="row" spacing={0.5} sx={{ bgcolor: 'action.hover', p: 0.5, borderRadius: 1 }}>
            <Button
              size="small"
              variant={!isVideoMode ? 'contained' : 'text'}
              onClick={() => setIsVideoMode(false)}
              disabled={isRecording}
            >
              Photo
            </Button>
            <Button
              size="small"
              variant={isVideoMode ? 'contained' : 'text'}
              onClick={() => setIsVideoMode(true)}
              disabled={isRecording}
            >
              Video
            </Button>
          </Stack>
        )}
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          position: 'relative',
          bgcolor: 'black',
          minHeight: 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {loading && (
          <Stack spacing={2} alignItems="center" sx={{ color: 'white' }}>
            <CircularProgress color="inherit" />
            <Typography variant="body2">Starting camera...</Typography>
          </Stack>
        )}

        {error && (
          <Box sx={{ p: 3, width: '100%' }}>
            <Alert severity="error" sx={{ bgcolor: 'error.darker', color: 'white' }}>
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && (
          <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            {renderContent()}

            {isRecording && (
              <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 10 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ bgcolor: 'rgba(0,0,0,0.6)', px: 1.5, py: 0.5, borderRadius: 2, color: 'white' }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      bgcolor: 'error.main',
                      borderRadius: '50%',
                      animation: 'pulse 1.5s infinite',
                    }}
                  />
                  <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                    {formatTime(recordingTime)}
                  </Typography>
                </Stack>
              </Box>
            )}
            <style>
                {`
                @keyframes pulse {
                    0% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(1.2); }
                    100% { opacity: 1; transform: scale(1); }
                }
                `}
            </style>
          </Box>
        )}

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={loading || isRecording}>
          Cancel
        </Button>

        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>{renderActions()}</Box>
      </DialogActions>
    </Dialog>
  );
}

WebcamDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onCapture: PropTypes.func,
  uploadSource: PropTypes.string,
  accept: PropTypes.object,
};
